import { AccessControlType } from '@squaredup/constants';
import type { DashboardType, DashboardVariable } from '@squaredup/dashboards';
import { DataStreamBaseTileConfig, DataStreamScope, resolveDataStreamScopeConfig } from '@squaredup/data-streams';
import { buildQuery } from '@squaredup/graph';
import { DashboardId } from '@squaredup/ids';
import { UnresolvedTimeframe } from '@squaredup/timeframes';
import { hasHandlebarsPlaceholders } from '@squaredup/utilities';
import { useLinkedPluginConfigs } from 'components/hooks/useLinkedPluginConfigs';
import { usePluginPermissions } from 'components/hooks/usePluginPermissions';
import { useWorkspacePermissions } from 'components/hooks/useWorkspacePermissions';
import { useDashboardContext } from 'contexts/DashboardContext';
import { useDataStreamWorkspaceContext } from 'contexts/DataStreamWorkspaceContext';
import {
    getDataStreamDefinition,
    useDataStreamFromTileConfig
} from 'dashboard-engine/hooks/useDataStreamFromTileConfig';
import { useParametersContext } from 'dashboard-engine/hooks/useParametersContext';
import { useVariableContext } from 'dashboard-engine/hooks/useVariableContext';
import stringify from 'fast-json-stable-stringify';
import { cloneDeep } from 'lodash';
import { useDashboardsWithFolders } from 'queries/hooks/useDashboardsWithFolders';
import { useDataStreamDefinitionsForWorkspace } from 'queries/hooks/useDataStreamDefinitions';
import { useWorkspaces } from 'queries/hooks/useWorkspaces';
import { dashboardQueryKeys } from 'queries/queryKeys/dashboardKeys';
import { dataStreamDefinitionQueryKeys } from 'queries/queryKeys/dataStreamDefinitionKeys';
import { workspaceQueryKeys } from 'queries/queryKeys/workspaceKeys';
import { replaceById } from 'queries/utils/replaceById';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { Create, Get, Update } from 'services/DashboardService';
import type { DataStream } from 'services/DataStreamDefinitionService';
import { resolveDataSourceConfig } from 'services/DataStreamService';
import { NO_ACTIVE_WORKSPACE } from 'services/WorkspaceUtil';
import useAddDataSources from 'ui/editor/dataStream/TileEditor/steps/DataSourceModal/useAddDatasources';
import { generateDashboardForTile, generateTileForConfig } from './generateTileConfig';
import { useTileDatasources } from './useTileDatasources';

export const CREATE_NEW_DASHBOARD = 'create_new_dashboard' as const;

type SaveToDashboardFormData = {
    targetWorkspace: string;
    targetDashboard: string;
};

type WorkspaceOption = {
    label: string;
    value: string;
};

/**
 * Encapsulates the form logic and state for saving to a dashboard
 * @param config Tile config
 * @returns Form state and data
 */
export const useSaveToDashboardForm = (
    config: DataStreamBaseTileConfig,
    currentDashboard?: DashboardId['value'],
    workspaceOverride?: string,
    isGlobal?: boolean,
    variables: DashboardVariable[] = []
) => {
    const queryClient = useQueryClient();
    const { workspace: dataStreamWorkspace } = useDataStreamWorkspaceContext();
    const dashboardContext = useDashboardContext();
    const { dataStreamDefinition, streamDefinitionLoading } = useDataStreamFromTileConfig(config);
    const { data: streamDefinitions = [], isFetched: streamDefinitionLoaded } = useDataStreamDefinitionsForWorkspace();
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();

    const workspace = workspaceOverride ?? dataStreamWorkspace;

    // Fetch workspaces & permissions
    const { canWrite, isLoading: isLoadingWorkspacePermissions } = useWorkspacePermissions();
    const { canRead, isLoading: isLoadingPluginPermissions } = usePluginPermissions();
    const { data: workspaceWithWritePermissions, isLoading: isLoadingWorkspaces } = useWorkspaces({
        enabled: Boolean(!isLoadingWorkspacePermissions && canWrite),
        select: (data) => data?.filter((w) => canWrite(w))
    });

    const workspaceOptions = workspaceWithWritePermissions
        ?.map((w) => ({
            label: w.id === workspace ? `Current workspace (${w.displayName})` : w.displayName,
            value: w.id
        }))
        ?.sort((a: WorkspaceOption, b: WorkspaceOption) => a.label.localeCompare(b.label));

    const tileDatasources = useTileDatasources(config, isGlobal ? NO_ACTIVE_WORKSPACE : workspace);
    const currentWorkspace = workspaceWithWritePermissions?.find(({ id }) => id === workspace);
    const canWriteToCurrentWorkspace = currentWorkspace ? canWrite(currentWorkspace) : undefined;

    // Setup form
    const form = useForm<SaveToDashboardFormData>({
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {
            targetWorkspace: canWriteToCurrentWorkspace ? workspace ?? '' : undefined,
            targetDashboard: CREATE_NEW_DASHBOARD
        }
    });

    const {
        formState: { isValid, errors, isSubmitting },
        handleSubmit,
        watch,
        resetField
    } = form;

    // Setup watcher for selected workspace ID
    const selectedWorkspace = watch('targetWorkspace', workspace || undefined);
    const selectedWorkspaceDisplayName = workspaceOptions?.find(({ value }) => value === selectedWorkspace)?.label;

    const { addDataSources } = useAddDataSources({
        workspaceId: selectedWorkspace,
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries(dataStreamDefinitionQueryKeys.forAllWorkspaces()),
                queryClient.invalidateQueries(workspaceQueryKeys.list)
            ]);
        }
    });

    useEffect(() => resetField('targetDashboard'), [selectedWorkspace, resetField]);

    // Load linked plugins
    const { data: linkedPlugins, isLoading: isLoadingLinkedPluginConfigs } = useLinkedPluginConfigs(
        selectedWorkspace === NO_ACTIVE_WORKSPACE ? undefined : selectedWorkspace
    );

    const areDatasourcesLinked = tileDatasources?.every(
        (configDatasource) => linkedPlugins?.some((l) => !configDatasource || l.id === configDatasource)
    );

    const areDatasourcesPermitted = tileDatasources.every(
        (tileDatasource) => !tileDatasource || canRead(tileDatasource)
    );

    // Fetch dashboards for the given workspace ID

    const { isLoading: isLoadingDashboards, data: dashboards } = useDashboardsWithFolders();

    const availableDashboards = dashboards
        ?.filter((dashboard) => (currentDashboard ? dashboard.id !== currentDashboard : true))
        .filter((dashboard) => dashboard.workspaceId === selectedWorkspace)
        .map((d) => ({
            folderPath: d.folderPath,
            displayName: d.displayName,
            label: d?.folderPath?.length ? `${d.folderPath.join(' / ')} / ${d.displayName}` : d.displayName,
            value: d.id
        }));

    const hadHandleBars = configHasHandlebars(config, streamDefinitions, streamDefinitionLoaded);
    const handlebarReplacementContext = {
        ...useVariableContext(config),
        ...useParametersContext(config, dataStreamDefinition)
    };

    // Setup mutation handler for saving
    const handleSave = useMutation(async ({ targetWorkspace, targetDashboard }: SaveToDashboardFormData) => {
        /**
         * Remove any access control permissions as the addition of datasources to the target
         * workspace removes the need for it
         */
        const { accessControlType, ...dataStreamConfig } =
            config.dataStream as DataStreamBaseTileConfig['dataStream'] & { accessControlType: AccessControlType };

        const configToSave = cloneDeep({ ...config, dataStream: dataStreamConfig });

        // Handle SQL tiles by mutating sub datasets if necessary
        const isSql =
            configToSave.dataStream.id === 'datastream-sql' && configToSave.dataStream.dataSourceConfig?.tables;
        if (isSql) {
            for (const table of configToSave.dataStream.dataSourceConfig?.tables ?? []) {
                const tableConfig = table?.config;
                if (!tableConfig?.dataStream) {
                    continue;
                }

                // Resolve any handlebar placeholders
                if (hadHandleBars && configHasHandlebars(tableConfig, streamDefinitions, streamDefinitionLoaded)) {
                    tableConfig.dataStream.dataSourceConfig = await resolveExpressionsInConfig(
                        tableConfig,
                        dashboardContext.timeframe,
                        handlebarReplacementContext
                    );
                }

                // Strip variables from table config, and replace with whole scope or individual objects
                handleVariablesInScope(tableConfig, variables);
            }
        }

        // Resolve any handlebar placeholders, as they may contain variables that the destination may not have access to
        if (hadHandleBars && !isSql) {
            configToSave.dataStream.dataSourceConfig = await resolveExpressionsInConfig(
                configToSave,
                dashboardContext.timeframe,
                handlebarReplacementContext
            );
        }

        // Strip variables from config, and replace with either the whole scope (if all selected)
        // or individual objects
        handleVariablesInScope(configToSave, variables);

        setIsSaving(true);
        if (targetDashboard === CREATE_NEW_DASHBOARD) {
            // Create new dashboard
            const created = await Create(targetWorkspace, undefined, generateDashboardForTile(configToSave));

            // Add any missing data sources
            if (!areDatasourcesLinked && tileDatasources.length) {
                await addDataSources(tileDatasources);
            }

            navigate('/dashboard/' + created.id);
        } else {
            // Fetch and update existing dashboard
            const existing = (await Get(targetDashboard)) as DashboardType;
            const y = Math.max(...existing.content.contents.map((t) => t.y + t.h));
            const tile = generateTileForConfig(configToSave);
            tile.y = y;
            existing.content.contents.push(tile);

            // Save, update cached data and navigate
            const updated = await Update(targetDashboard, existing);

            // Optimistically update to the new value in list
            queryClient.setQueryData<DashboardType[]>(dashboardQueryKeys.list, (data) =>
                replaceById(data, updated as DashboardType)
            );

            // Add any missing data sources
            if (!areDatasourcesLinked && tileDatasources.length) {
                await addDataSources(tileDatasources);
            }

            navigate(`/dashboard/${targetDashboard}?tile=${tile.i}`);
        }
    });

    const isLoading =
        isLoadingWorkspaces ||
        isLoadingWorkspacePermissions ||
        isLoadingPluginPermissions ||
        isLoadingDashboards ||
        streamDefinitionLoading;

    const isDisabled =
        isLoading ||
        isSubmitting ||
        !isValid ||
        (!areDatasourcesPermitted && !areDatasourcesLinked) ||
        Object.keys(errors).length > 0;

    const formHandler = handleSubmit((data) => handleSave.mutateAsync(data));

    return {
        isLoading,
        isLoadingDashboards,
        isLoadingLinkedPluginConfigs,
        isSubmitting,
        isDisabled,
        isSaving,
        form,
        workspaces: workspaceOptions,
        selectedWorkspace,
        selectedWorkspaceDisplayName,
        dashboards: availableDashboards,
        areDatasourcesLinked,
        areDatasourcesPermitted,
        areExpressionsPresent: hadHandleBars,
        formHandler
    };
};

const removeVariableFromScope = (
    scope: DataStreamBaseTileConfig['scope'],
    variables: DashboardVariable[]
): DataStreamScope | undefined => {
    if (scope === undefined || !('scope' in scope) || !scope.scope || !scope.workspace) {
        return undefined;
    }

    const variableMatchingTileScope = variables.find((v) => v.scopeId === scope.scope);

    // All objects are selected, just use the scope as-is
    if (variableMatchingTileScope?.selectedAll) {
        return {
            scope: scope.scope,
            workspace: scope.workspace
        };
    }

    if ((variableMatchingTileScope?.selectedObjects.length ?? 0) < 1) {
        // Strip scope so the tile counts as not having any objects configured
        return undefined;
    }

    // replace scope with specific object selection
    const scopeContents = {
        ids: variableMatchingTileScope?.selectedObjects.map((o) => o.id) ?? []
    };

    const scopeQuery = buildQuery(scopeContents, '');

    return {
        query: scopeQuery.gremlinQuery,
        bindings: scopeQuery.bindings,
        queryDetail: scopeContents
    };
};

const resolveExpressionsInConfig = async (
    config: Partial<DataStreamBaseTileConfig>,
    dashboardTimeframe: UnresolvedTimeframe,
    variableContext: Record<string, unknown>
) => {
    if (config.dataStream?.id) {
        const scope = resolveDataStreamScopeConfig(config.scope);
        const resolvedConfig = await resolveDataSourceConfig(
            config.dataStream.id,
            scope,
            config.dataStream?.dataSourceConfig,
            config.timeframe || dashboardTimeframe,
            variableContext
        );
        return resolvedConfig;
    }

    return config.dataStream?.dataSourceConfig;
};

const handleVariablesInScope = (config: Partial<DataStreamBaseTileConfig>, variables: DashboardVariable[]) => {
    if (config.variables?.length) {
        config.scope = removeVariableFromScope(config.scope, variables);
        config.variables = undefined;
    }
};

const configHasHandlebars = (
    config: Partial<DataStreamBaseTileConfig>,
    datastreams: DataStream[],
    datastreamsLoaded: boolean
): boolean => {
    if (config.dataStream?.id === 'datastream-sql') {
        // need to check all of the tables instead
        return (config.dataStream.dataSourceConfig?.tables ?? []).some(
            (table) => table?.config && configHasHandlebars(table.config, datastreams, datastreamsLoaded)
        );
    }

    const { dataStreamDefinition } = getDataStreamDefinition(config, datastreams, datastreamsLoaded);
    const resolveExpressions =
        dataStreamDefinition?.definition.options?.bypassDataSourceConfigExpressionResolution !== true;

    return resolveExpressions && hasHandlebarsPlaceholders(stringify(config?.dataStream?.dataSourceConfig));
};
